import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getPosCommissionsReport, getBankCommissions, setBankCommission, getStores } from '../api/api';
import { 
  CreditCard, DollarSign, Percent, Building2, Calendar, 
  ChevronLeft, ChevronRight, Settings, Save, TrendingDown,
  Banknote, Phone, Receipt, Coins, Download
} from 'lucide-react';

const PosCommissionsReport = () => {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStore, setSelectedStore] = useState('');
  const [stores, setStores] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  const [bankConfigs, setBankConfigs] = useState([]);
  const [editingStore, setEditingStore] = useState(null);
  const [editForm, setEditForm] = useState({ bank_name: '', commission_rate: '' });

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  useEffect(() => {
    loadStores();
    loadBankConfigs();
  }, []);

  useEffect(() => {
    loadReport();
  }, [selectedMonth, selectedYear, selectedStore]);

  const loadStores = async () => {
    try {
      const data = await getStores();
      setStores(data);
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const loadBankConfigs = async () => {
    try {
      const data = await getBankCommissions();
      setBankConfigs(data);
    } catch (error) {
      console.error('Error loading bank configs:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await getPosCommissionsReport(
        selectedStore || null,
        selectedYear,
        selectedMonth
      );
      setReport(data);
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const handleSaveCommission = async (storeId) => {
    try {
      await setBankCommission(storeId, editForm.bank_name, parseFloat(editForm.commission_rate));
      setEditingStore(null);
      loadBankConfigs();
      loadReport();
    } catch (error) {
      console.error('Error saving commission:', error);
    }
  };

  const startEdit = (store) => {
    const config = bankConfigs.find(c => c.store_id === store.id);
    setEditingStore(store.id);
    setEditForm({
      bank_name: config?.bank_name || '',
      commission_rate: config?.commission_rate || 1.5
    });
  };

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-blue-800 rounded-xl shadow-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CreditCard size={28} />
              Reporte de Ventas y Comisiones POS
            </h1>
            <p className="text-slate-300 mt-1">Desglose de ventas por método de pago y comisiones bancarias</p>
          </div>
          
          {/* Controles */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Selector de Tienda */}
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
            >
              <option value="">Todas las tiendas</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
            
            {/* Selector de Mes */}
            <div className="flex items-center space-x-2 bg-slate-700/50 rounded-lg px-3 py-2">
              <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-slate-600 rounded">
                <ChevronLeft size={20} />
              </button>
              <span className="min-w-[120px] text-center font-medium">
                {months[selectedMonth - 1]} {selectedYear}
              </span>
              <button onClick={() => changeMonth(1)} className="p-1 hover:bg-slate-600 rounded">
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Botón de Configuración */}
            <button
              onClick={() => setShowConfig(!showConfig)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showConfig ? 'bg-amber-500 text-white' : 'bg-slate-700 hover:bg-slate-600'
              }`}
            >
              <Settings size={18} />
              Configurar Comisiones
            </button>
          </div>
        </div>
      </div>

      {/* Panel de Configuración de Comisiones */}
      {showConfig && (
        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-amber-200">
          <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Percent size={20} className="text-amber-500" />
            Configurar Comisiones Bancarias por Tienda
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Configure el porcentaje de comisión que cobra cada banco. Ejemplo: 1.5% significa Q1.50 por cada Q100.
          </p>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Tienda</th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Banco</th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Comisión (%)</th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {stores.map(store => {
                  const config = bankConfigs.find(c => c.store_id === store.id);
                  const isEditing = editingStore === store.id;
                  
                  return (
                    <tr key={store.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{store.name}</td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.bank_name}
                            onChange={(e) => setEditForm({ ...editForm, bank_name: e.target.value })}
                            placeholder="Nombre del banco"
                            className="px-3 py-1 border rounded w-full"
                          />
                        ) : (
                          <span className={config ? 'text-slate-800' : 'text-slate-400 italic'}>
                            {config?.bank_name || 'Sin configurar'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.1"
                            value={editForm.commission_rate}
                            onChange={(e) => setEditForm({ ...editForm, commission_rate: e.target.value })}
                            className="px-3 py-1 border rounded w-24"
                          />
                        ) : (
                          <span className={`px-2 py-1 rounded ${config ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`}>
                            {config?.commission_rate || 1.5}%
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveCommission(store.id)}
                              className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1"
                            >
                              <Save size={14} /> Guardar
                            </button>
                            <button
                              onClick={() => setEditingStore(null)}
                              className="px-3 py-1 bg-slate-300 rounded hover:bg-slate-400"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(store)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            Editar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resumen Totales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 uppercase font-semibold">Ventas POS (Bruto)</p>
              <p className="text-3xl font-bold text-blue-600">
                Q{report?.totals?.pos_bruto?.toLocaleString() || '0.00'}
              </p>
            </div>
            <CreditCard size={40} className="text-blue-200" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 uppercase font-semibold">Comisión Bancaria Total</p>
              <p className="text-3xl font-bold text-red-500">
                -Q{report?.totals?.comision_total?.toLocaleString() || '0.00'}
              </p>
            </div>
            <TrendingDown size={40} className="text-red-200" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 uppercase font-semibold">POS Neto (Recibido)</p>
              <p className="text-3xl font-bold text-green-600">
                Q{report?.totals?.pos_neto?.toLocaleString() || '0.00'}
              </p>
            </div>
            <DollarSign size={40} className="text-green-200" />
          </div>
        </div>
      </div>

      {/* Detalle por Tienda */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Building2 size={20} />
            Detalle por Tienda - {months[selectedMonth - 1]} {selectedYear}
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Tienda</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase">
                  <div className="flex items-center justify-end gap-1"><Banknote size={14} /> Efectivo</div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase">
                  <div className="flex items-center justify-end gap-1"><CreditCard size={14} /> POS Bruto</div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase">
                  <div className="flex items-center justify-end gap-1"><Percent size={14} /> Comisión</div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase bg-green-50">
                  <div className="flex items-center justify-end gap-1"><DollarSign size={14} /> POS Neto</div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase">
                  <div className="flex items-center justify-end gap-1"><Phone size={14} /> Recargas</div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase">
                  <div className="flex items-center justify-end gap-1"><Receipt size={14} /> Recaudo</div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase">
                  <div className="flex items-center justify-end gap-1"><Coins size={14} /> Sobrantes</div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase bg-blue-50">Total Ventas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {report?.stores?.map((store, index) => (
                <tr key={store.store_id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-semibold text-slate-800">{store.store_name}</p>
                      <p className="text-xs text-slate-500">
                        {store.bank_name} ({store.commission_rate}%)
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-emerald-600">
                    Q{store.sales_breakdown.efectivo.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-blue-600">
                    Q{store.sales_breakdown.pos_bruto.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-red-500">
                    -Q{store.sales_breakdown.pos_comision.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-green-600 bg-green-50">
                    Q{store.sales_breakdown.pos_neto.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-amber-600">
                    Q{store.sales_breakdown.recargas.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-purple-600">
                    Q{store.sales_breakdown.recaudo.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-orange-600">
                    Q{store.sales_breakdown.sobrantes.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-slate-800 bg-blue-50">
                    Q{store.total_ventas.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-800 text-white font-bold">
                <td className="px-4 py-3">TOTALES</td>
                <td className="px-4 py-3 text-right">
                  Q{report?.stores?.reduce((acc, s) => acc + s.sales_breakdown.efectivo, 0).toLocaleString() || 0}
                </td>
                <td className="px-4 py-3 text-right">
                  Q{report?.totals?.pos_bruto?.toLocaleString() || 0}
                </td>
                <td className="px-4 py-3 text-right text-red-300">
                  -Q{report?.totals?.comision_total?.toLocaleString() || 0}
                </td>
                <td className="px-4 py-3 text-right text-green-300">
                  Q{report?.totals?.pos_neto?.toLocaleString() || 0}
                </td>
                <td className="px-4 py-3 text-right">
                  Q{report?.stores?.reduce((acc, s) => acc + s.sales_breakdown.recargas, 0).toLocaleString() || 0}
                </td>
                <td className="px-4 py-3 text-right">
                  Q{report?.stores?.reduce((acc, s) => acc + s.sales_breakdown.recaudo, 0).toLocaleString() || 0}
                </td>
                <td className="px-4 py-3 text-right">
                  Q{report?.stores?.reduce((acc, s) => acc + s.sales_breakdown.sobrantes, 0).toLocaleString() || 0}
                </td>
                <td className="px-4 py-3 text-right">
                  Q{report?.stores?.reduce((acc, s) => acc + s.total_ventas, 0).toLocaleString() || 0}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Leyenda */}
      <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-600">
        <p className="font-semibold mb-2">Notas:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>POS Bruto:</strong> Total de ventas realizadas con tarjeta antes de comisiones</li>
          <li><strong>Comisión:</strong> Porcentaje retenido por el banco (configurable por tienda)</li>
          <li><strong>POS Neto:</strong> Monto real que se recibe en la cuenta bancaria después de la comisión</li>
          <li><strong>Efectivo Disponible:</strong> Efectivo + Recargas + Recaudo + Sobrantes (dinero físico en caja)</li>
        </ul>
      </div>
    </div>
  );
};

export default PosCommissionsReport;
